import { Modal, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { FC } from 'react';

import { BRAND_COLORS } from '../../../constants/styles';
import {
  DonationProductRecord,
  DonationRecord,
} from '../../../types/phlebotomist-dashboard';

const { Text, Title } = Typography;

const FAILURE_REASON_LABELS: Record<string, string> = {
  Clots: 'Clots',
  LowVolume: 'Low Volume',
  Hemolyzed: 'Hemolyzed',
  SealIssue: 'Seal Issue',
  BrokenBag: 'Broken Bag',
  RedCellContamination: 'Red Cell Contamination',
  DonorHealthIssue: 'Donor Health Issue',
  Lipemic: 'Lipemic',
  Other: 'Other',
  Timeout: 'Unsuccessful',
  Expired: 'Expired',
};

const STATUS_COLORS: Record<string, string> = {
  Complete: BRAND_COLORS.secondary.warmBeige,
  'DBL→SGL': BRAND_COLORS.primary.deepNavy,
  Ineligible: BRAND_COLORS.primary.red,
  Partial: BRAND_COLORS.primary.royalBlue,
  Failed: '#ff4d4f',
  Pending: '#faad14',
};

const PRODUCT_STATUS_COLORS: Record<string, string> = {
  Collected: '#1890ff',
  Processed: '#722ed1',
  Sold: '#52c41a',
  Pending: '#faad14',
  Available: '#13c2c2',
  Discarded: '#ff4d4f',
  Cryoprecipitate: '#eb2f96',
};

const VEIN_LABELS: Record<string, string> = {
  LefJugular: 'Left Jugular',
  RightJugular: 'Right Jugular',
  Both: 'Both',
  NotApplicable: 'N/A',
};

interface IDonationDetailModalProps {
  donation: DonationRecord | null;
  visible: boolean;
  onClose: () => void;
}

const donationColumns: ColumnsType<Record<string, unknown>> = [
  { title: 'ID', dataIndex: 'shortId', key: 'shortId', width: 70 },
  {
    title: 'Approved mL',
    dataIndex: 'approvedML',
    key: 'approvedML',
    width: 100,
  },
  { title: 'Actual mL', dataIndex: 'actualML', key: 'actualML', width: 90 },
  {
    title: 'Collection Set',
    dataIndex: 'collectionSet',
    key: 'collectionSet',
    width: 130,
  },
  { title: 'Vein Side', dataIndex: 'veinSide', key: 'veinSide', width: 120 },
  {
    title: 'Donation Unit',
    dataIndex: 'donationUnit',
    key: 'donationUnit',
    width: 110,
  },
  {
    title: 'Ineligible Reason',
    dataIndex: 'ineligibleReason',
    key: 'ineligibleReason',
    width: 130,
  },
];

const productColumns: ColumnsType<DonationProductRecord & { key: number }> = [
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 120,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (status: string) => (
      <Tag color={PRODUCT_STATUS_COLORS[status] || '#999'}>{status}</Tag>
    ),
  },
  {
    title: 'Order ID',
    dataIndex: 'orderId',
    key: 'orderId',
    width: 100,
    render: (val: number | null) => val ?? '—',
  },
  {
    title: 'Discard Reason',
    dataIndex: 'discardReason',
    key: 'discardReason',
    render: (reason: string | null) =>
      reason ? (
        <Tag color="#ff4d4f">{FAILURE_REASON_LABELS[reason] || reason}</Tag>
      ) : (
        '—'
      ),
  },
];

export const DonationDetailModal: FC<IDonationDetailModalProps> = ({
  donation,
  visible,
  onClose,
}) => {
  const totalProducts = donation
    ? donation.actualPRBC + donation.actualFFPSingle + donation.actualFFPDouble
    : 0;
  const hasFailure = donation
    ? totalProducts === 0 && donation.status !== 'Pending'
    : false;

  const donationDataSource = donation
    ? [
        {
          key: 'donation',
          shortId: donation.shortId,
          approvedML: donation.approvedML,
          actualML: donation.actualML ?? '—',
          collectionSet: donation.collectionSet || '—',
          veinSide:
            VEIN_LABELS[donation.veinSide || ''] || donation.veinSide || '—',
          donationUnit: donation.donationUnit || '—',
          ineligibleReason: donation.ineligibleReason || '—',
        },
      ]
    : [];

  const productDataSource = donation
    ? (donation.products || []).map((p, i) => ({ ...p, key: i }))
    : [];

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      title={
        donation ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>
                Donation {donation.shortId ? `#${donation.shortId}` : ''} —{' '}
                {donation.donorName}
              </span>
              <Tag color={STATUS_COLORS[donation.status] || '#999'}>
                {donation.status}
              </Tag>
            </div>
            <Text
              type="secondary"
              style={{ fontSize: 12, fontWeight: 'normal' }}
            >
              {donation.id}
            </Text>
          </div>
        ) : null
      }
      width={800}
      destroyOnClose
    >
      {/* Failure reason banner for zero-product donations */}
      {hasFailure && donation && donation.failureReasons.length > 0 && (
        <div
          style={{
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 6,
            padding: '12px 16px',
            marginBottom: 16,
            textAlign: 'center',
          }}
        >
          <Title level={5} style={{ margin: 0, color: '#ff4d4f' }}>
            {donation.failureReasons
              .map((r) => FAILURE_REASON_LABELS[r] || r)
              .join(', ')}
          </Title>
        </div>
      )}

      {/* Ineligible reason banner */}
      {donation?.status === 'Ineligible' && donation.ineligibleReason && (
        <div
          style={{
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 6,
            padding: '12px 16px',
            marginBottom: 16,
            textAlign: 'center',
          }}
        >
          <Title
            level={5}
            style={{ margin: 0, color: BRAND_COLORS.primary.red }}
          >
            Ineligible: {donation.ineligibleReason}
          </Title>
        </div>
      )}

      {/* Donation data table */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Donation Details
      </Text>
      <Table
        columns={donationColumns}
        dataSource={donationDataSource}
        pagination={false}
        size="small"
        scroll={{ x: 750 }}
        style={{ marginBottom: 20 }}
      />

      {/* Examination Notes */}
      {donation?.examinationNotes && (
        <div
          style={{
            background: '#f6f8fa',
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 20,
          }}
        >
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Examination Notes
          </Text>
          <Text style={{ whiteSpace: 'pre-wrap' }}>
            {donation.examinationNotes}
          </Text>
        </div>
      )}

      {/* Products table */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Products ({productDataSource.length})
      </Text>
      {productDataSource.length > 0 ? (
        <Table
          columns={productColumns}
          dataSource={productDataSource}
          pagination={false}
          size="small"
        />
      ) : (
        <Text type="secondary" style={{ fontStyle: 'italic' }}>
          No products recorded for this donation.
        </Text>
      )}
    </Modal>
  );
};

export default DonationDetailModal;
